import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axiosInstance';
import { ArrowUp, ArrowDown, ArrowRight, CaretUp, CaretDown, FileText, X, CalendarBlank, ClockCountdown } from '@phosphor-icons/react';
import { PieChart, Pie, Cell, Tooltip as RechartTooltip, ResponsiveContainer } from 'recharts';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0
}).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleString('en-US', {
  month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'
}) : '—';

const PRIORITY_COLORS = {
  1: 'bg-gray-100 text-gray-600 border-gray-300',
  2: 'bg-blue-50 text-blue-700 border-blue-200',
  3: 'bg-amber-50 text-amber-700 border-amber-200',
  4: 'bg-orange-50 text-orange-700 border-orange-200',
  5: 'bg-red-50 text-red-700 border-red-200',
};

const STATUS_STYLES = {
  PENDING:   'bg-amber-50 border-amber-200 text-amber-700',
  COMPLETED: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  NETTED:    'bg-indigo-50 border-indigo-200 text-indigo-700',
};

const TABS = [
  { key: 'PENDING',   label: 'Pending'   },
  { key: 'COMPLETED', label: 'Completed' },
  { key: 'NETTED',    label: 'Netted'    },
];

export const PaymentsPage = () => {
  const [payments, setPayments]           = useState([]);
  const [channels, setChannels]           = useState([]);
  const [activeTab, setActiveTab]         = useState('PENDING');
  const [filteredChannel, setFilteredChannel] = useState('all');
  const [filteredQueue, setFilteredQueue] = useState('all');
  const [expandedId, setExpandedId]       = useState(null);
  const [updatingPriority, setUpdatingPriority] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = { status: activeTab };
      if (filteredChannel !== 'all') params.channel_id = filteredChannel;
      if (filteredQueue   !== 'all') params.queue_type  = filteredQueue;
      const [pRes, chRes] = await Promise.all([
        api.get(`${API}/payments`, { params }),
        api.get(`${API}/channels`)
      ]);
      setPayments(pRes.data);
      setChannels(chRes.data);
    } catch (e) { console.error('Fetch data error:', e); }
  }, [activeTab, filteredChannel, filteredQueue]);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 3000);
    return () => clearInterval(id);
  }, [fetchData]);

  const getChannelCode = (id) => channels.find(c => c.id === id)?.code || '—';

  const handlePriorityChange = async (payment, delta) => {
    const next = Math.max(1, Math.min(5, payment.priority + delta));
    if (next === payment.priority) return;
    setUpdatingPriority(payment.id);
    try {
      await api.put(`${API}/payments/${payment.id}/priority`, { priority: next });
      toast.success(`Priority → P${next}`);
      fetchData();
    } catch { toast.error('Failed to update priority'); }
    finally   { setUpdatingPriority(null); }
  };

  const current = payments.filter(p => p.queue_type === 'CURRENT').length;
  const future  = payments.filter(p => p.queue_type === 'FUTURE').length;
  const credits = payments.filter(p => p.payment_type === 'CREDIT').length;
  const debits  = payments.filter(p => p.payment_type === 'DEBIT').length;
  const debitAmt  = payments.filter(p => p.payment_type === 'DEBIT').reduce((s, p) => s + p.amount, 0);

  const channelDonut = channels.map((ch, i) => {
    const COLORS = ['#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];
    const count = payments.filter(p => p.channel_id === ch.id).length;
    return { name: ch.code, value: count, color: COLORS[i % COLORS.length] };
  }).filter(d => d.value > 0);

  return (
    <div className="space-y-4 fade-in">

      {/* Header */}
      <div>
        <h1 className="text-xl font-black tracking-tight text-gray-900">Payment Viewer</h1>
        <p className="text-xs text-gray-500 mt-0.5 uppercase tracking-wide font-medium">
          Individual payments — click a row to expand details
        </p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Showing',       value: payments.length, color: 'text-gray-900' },
          { label: 'Current Queue', value: current, color: 'text-emerald-600' },
          { label: 'Future Queue',  value: future,  color: 'text-indigo-600' },
          { label: 'Status',        value: activeTab,       color: 'text-gray-500' },
        ].map(s => (
          <Card key={s.label} className="p-3 bg-white border border-gray-200 rounded-lg overflow-hidden">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-1">{s.label}</p>
            <p className={`text-sm font-black font-mono truncate ${s.color}`}>{s.value}</p>
          </Card>
        ))}
      </div>

      {/* Doughnut Chart — Channel breakdown */}
      {payments.length > 0 && (
        <Card className="p-4 bg-white border border-gray-200 rounded-lg" data-testid="payment-channel-donut">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-3">Payment Distribution by Channel</p>
          <div className="flex items-center gap-6">
            <ResponsiveContainer width={140} height={140}>
              <PieChart>
                <Pie data={channelDonut} cx="50%" cy="50%" innerRadius={42} outerRadius={62} dataKey="value" strokeWidth={2}>
                  {channelDonut.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                </Pie>
                <RechartTooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '11px', fontFamily: 'JetBrains Mono' }}
                  formatter={(v, name) => [`${v} payments`, name]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-x-6 gap-y-2 flex-1">
              {channelDonut.map(d => (
                <div key={d.name} className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                  <span className="text-sm font-mono font-semibold text-gray-700">{d.name}</span>
                  <span className="text-sm font-black font-mono text-gray-900">{d.value}</span>
                </div>
              ))}
              <p className="w-full text-xs text-gray-400 font-mono mt-1">Total: {payments.length}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Tab + Filters row */}
      <div className="flex flex-wrap items-center gap-3">
        {/* 3 Status Tabs */}
        <div className="flex gap-0.5 p-1 bg-gray-100 rounded-lg">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setExpandedId(null); }}
              className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                activeTab === tab.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Queue filter */}
        <div className="flex gap-1">
          {[['all','All Queues'],['CURRENT','Current'],['FUTURE','Future']].map(([k,l]) => (
            <button
              key={k}
              onClick={() => setFilteredQueue(k)}
              className={`px-3 py-1 text-xs font-semibold rounded-full border transition-colors ${
                filteredQueue === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
              }`}
              data-testid={`queue-filter-${k}`}
            >
              {l}
            </button>
          ))}
        </div>

        {/* Channel filter */}
        <select
          value={filteredChannel}
          onChange={e => setFilteredChannel(e.target.value)}
          className="h-7 text-xs border border-gray-200 rounded-md px-2 bg-white text-gray-700"
          data-testid="channel-filter"
        >
          <option value="all">All Channels</option>
          {channels.map(ch => <option key={ch.id} value={ch.id}>{ch.name}</option>)}
        </select>

        <span className="text-xs text-gray-400 font-mono ml-auto">{payments.length} result{payments.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Payment List — no horizontal scroll */}
      <Card className="p-0 overflow-hidden bg-white border border-gray-200 rounded-lg" data-testid="payments-list">
        {payments.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-sm font-medium text-gray-400">No {activeTab.toLowerCase()} payments</p>
            <p className="text-xs text-gray-400 mt-1">Initialize and start the simulation</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {payments.map(p => {
              const isExpanded = expandedId === p.id;
              const isPending  = p.status === 'PENDING';

              return (
                <div key={p.id} data-testid={`payment-${p.id}`}>
                  {/* Main row */}
                  <div
                    className="flex flex-col px-4 py-3 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setExpandedId(isExpanded ? null : p.id)}
                  >
                    {/* Line 1: direction + amount + badges + priority */}
                    <div className="flex items-center gap-x-3 gap-y-1 flex-wrap">
                      {/* Direction */}
                      <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${p.payment_type === 'CREDIT' ? 'bg-emerald-100' : 'bg-red-100'}`}>
                        {p.payment_type === 'CREDIT'
                          ? <ArrowDown size={11} weight="bold" className="text-emerald-600" />
                          : <ArrowUp   size={11} weight="bold" className="text-red-600" />
                        }
                      </div>

                      {/* Amount */}
                      <span className={`text-sm font-black font-mono flex-shrink-0 ${p.payment_type === 'CREDIT' ? 'text-emerald-600' : 'text-red-600'}`}>
                        {fmt(p.amount)}
                      </span>

                      {/* Badges */}
                      <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${p.queue_type === 'CURRENT' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                          {p.queue_type === 'CURRENT' ? <CalendarBlank size={9} className="inline mr-1" /> : <ClockCountdown size={9} className="inline mr-1" />}
                          {p.queue_type}
                        </span>
                        <span className="text-xs font-mono font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5">
                          {getChannelCode(p.channel_id)}
                        </span>
                      </div>

                      {/* Priority + inline change */}
                      <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                        <span className={`text-xs font-black border rounded px-1.5 py-0.5 ${PRIORITY_COLORS[p.priority] || PRIORITY_COLORS[3]}`}>P{p.priority}</span>
                        {isPending && (
                          <div className="flex flex-col gap-0.5">
                            <button onClick={() => handlePriorityChange(p, 1)} disabled={p.priority >= 5 || updatingPriority === p.id} className="w-4 h-4 flex items-center justify-center rounded hover:bg-gray-200 disabled:opacity-30" data-testid={`priority-up-${p.id}`}>
                              <CaretUp size={8} weight="bold" />
                            </button>
                            <button onClick={() => handlePriorityChange(p, -1)} disabled={p.priority <= 1 || updatingPriority === p.id} className="w-4 h-4 flex items-center justify-center rounded hover:bg-gray-200 disabled:opacity-30" data-testid={`priority-down-${p.id}`}>
                              <CaretDown size={8} weight="bold" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Line 2: Sender → Receiver */}
                    <div className="flex items-center gap-2 mt-1.5 ml-9">
                      <span className="text-xs font-mono font-semibold text-gray-700 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">{p.sender}</span>
                      <ArrowRight size={12} weight="bold" className="text-gray-400 flex-shrink-0" />
                      <span className="text-xs font-mono font-semibold text-gray-700 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">{p.receiver}</span>
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="px-4 pb-4 bg-gray-50 border-t border-gray-100">
                      <div className="pt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        {[
                          ['ID', p.id?.slice(0, 12)],
                          ['Type', p.payment_type],
                          ['Status', p.status],
                          ['Netted', p.netted ? 'Yes' : 'No'],
                          ['Generated', fmtDate(p.generated_date)],
                          ['Delivery By', fmtDate(p.delivery_by_date)],
                          ['Receipt', fmtDate(p.receipt_date)],
                          ['Processed', fmtDate(p.processed_date)],
                        ].map(([label, val]) => (
                          <div key={label}>
                            <span className="text-gray-500 uppercase tracking-wide block mb-0.5">{label}</span>
                            <span className="font-mono text-gray-900">{val || '—'}</span>
                          </div>
                        ))}
                      </div>
                      {p.xml_data && (
                        <div className="mt-3">
                          <div className="flex items-center gap-1.5 mb-1">
                            <FileText size={12} weight="bold" className="text-gray-400" />
                            <span className="text-xs text-gray-500 uppercase tracking-wide font-semibold">XML</span>
                          </div>
                          <pre className="text-xs font-mono bg-gray-100 border border-gray-200 rounded-md p-3 overflow-x-auto max-h-28 leading-relaxed text-gray-700">{p.xml_data}</pre>
                        </div>
                      )}
                      <button onClick={() => setExpandedId(null)} className="mt-2 flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600">
                        <X size={10} weight="bold" /> Collapse
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};
